/* global debounce */

if (!customElements.get('search-form')) {
  class SearchForm extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = this.querySelector('.js-search-product-type-input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.placeholderViewport = null;
      this.placeholderTrack = null;
      this.addListeners();
      this.init();
    }

    addListeners() {
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.resetBtn.addEventListener('click', this.handleReset.bind(this));
    }

    init() {
      if ((this.input.dataset.placeholderTwo || this.input.dataset.placeholderThree)
        && (this.input.dataset.placeholderPromptsMob === 'true' || theme.mediaMatches.md)) {
        this.initAnimatedPlaceholder();
        this.typeInPlaceholders();
      }
    }

    /**
     * Adds a clipped visual placeholder to the mobile home search so long prompts can pan into view.
     * The real input placeholder remains in place for focus states and accessibility.
     */
    initAnimatedPlaceholder() {
      if (this.input.dataset.placeholderPromptsMob !== 'true'
        || !this.closest('.mastoner-home-showcase')) return;

      this.placeholderViewport = document.createElement('span');
      this.placeholderViewport.className = 'search__placeholder-viewport';
      this.placeholderViewport.setAttribute('aria-hidden', 'true');

      this.placeholderTrack = document.createElement('span');
      this.placeholderTrack.className = 'search__placeholder-track';
      this.placeholderViewport.appendChild(this.placeholderTrack);
      this.input.insertAdjacentElement('afterend', this.placeholderViewport);
      this.input.classList.add('search__input--animated-placeholder');

      const inputStyles = window.getComputedStyle(this.input);
      this.placeholderViewport.style.font = inputStyles.font;
      this.placeholderViewport.style.letterSpacing = inputStyles.letterSpacing;
      this.updateAnimatedPlaceholder(this.input.placeholder);

      this.input.addEventListener('focus', () => {
        this.setAnimatedPlaceholderSuspended(true);
      });
      this.input.addEventListener('blur', () => {
        if (this.input.value.length === 0) {
          this.setAnimatedPlaceholderSuspended(false);
          window.setTimeout(() => this.panAnimatedPlaceholder(), 150);
        }
      });
    }

    updateAnimatedPlaceholder(placeholder) {
      this.resetPlaceholderPan();
      this.input.setAttribute('placeholder', placeholder);
      if (this.placeholderTrack) this.placeholderTrack.textContent = placeholder;
    }

    setAnimatedPlaceholderSuspended(suspended) {
      if (!this.placeholderViewport) return;
      this.input.classList.toggle('search__input--animated-placeholder-suspended', suspended);
      if (suspended) this.resetPlaceholderPan();
    }

    resetPlaceholderPan() {
      if (this.placeholderTrack) {
        this.placeholderTrack.style.transition = 'none';
        this.placeholderTrack.style.transform = 'translateX(0)';
      }
    }

    panAnimatedPlaceholder() {
      if (!this.placeholderViewport
        || !this.placeholderTrack
        || this.input.value.length > 0
        || document.activeElement === this.input
        || !window.matchMedia('(max-width: 699px)').matches
        || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.resetPlaceholderPan();
      const overflow = this.placeholderTrack.scrollWidth - this.placeholderViewport.clientWidth;
      if (overflow <= 2) return;

      const distance = Math.ceil(overflow + 2);
      const duration = Math.min(1500, Math.max(900, distance * 10));
      this.placeholderTrack.getBoundingClientRect();
      this.placeholderTrack.style.transition = `transform ${duration}ms linear`;
      this.placeholderTrack.style.transform = `translateX(-${distance}px)`;
    }

    /**
     * Simulates the placeholder titles being typed in and the previous one deleted, on repeat
     */
    typeInPlaceholders() {
      const typingSpeed = this.input.dataset.typingSpeed || 100;
      const deletingSpeed = this.input.dataset.deletingSpeed || 60;
      const delayAfterDeleting = this.input.dataset.delayAfterDeleting || 500;
      const delayBeforeFirstDelete = this.input.dataset.delayBeforeFirstDelete || 2000;
      const delayAfterWordTyped = this.input.dataset.delayAfterWordTyped || 2400;
      const placeholders = [];

      if (this.input.placeholder) {
        placeholders.push(this.input.placeholder);
      }

      if (this.input.dataset.placeholderTwo) {
        placeholders.push(this.input.dataset.placeholderTwo);
      }

      if (this.input.dataset.placeholderThree) {
        placeholders.push(this.input.dataset.placeholderThree);
      }

      const typeInNextPlaceholder = async (placeholder) => {
        await new Promise((resolve) => {
          let currentPlaceholder = this.input.getAttribute('placeholder') || '';

          // If necessary remove the start of the word to type in if it already exists in the
          // placeholder
          let nextPlaceholder = (currentPlaceholder.length >= 3
              && placeholder.startsWith(currentPlaceholder))
            ? placeholder.replace(currentPlaceholder, '')
            : placeholder;

          const typingIntervalId = setInterval(() => {
            currentPlaceholder = this.input.getAttribute('placeholder') || '';
            this.updateAnimatedPlaceholder(currentPlaceholder + nextPlaceholder.charAt(0));
            if (nextPlaceholder.length === 1) {
              this.panAnimatedPlaceholder();
              resolve();
              clearInterval(typingIntervalId); // Stop typing
            } else {
              nextPlaceholder = nextPlaceholder.substring(1);
            }
          }, typingSpeed);
        });
      };

      const deleteCurrentPlaceholder = async (nextPlaceholder) => {
        await new Promise((resolve) => {
          let prevPlaceholder = this.input.getAttribute('placeholder') || '';
          this.resetPlaceholderPan();
          const deletionIntervalId = setInterval(() => {
            const newPlaceholder = prevPlaceholder.substring(0, prevPlaceholder.length - 1);
            this.updateAnimatedPlaceholder(newPlaceholder);
            prevPlaceholder = newPlaceholder;
            if (prevPlaceholder.length === 0
              || (prevPlaceholder.length >= 3 && nextPlaceholder.startsWith(prevPlaceholder))) {
              resolve();
              clearInterval(deletionIntervalId);
            }
          }, deletingSpeed);
        });
      };

      let startIndex = 0;
      const showNextPlaceholder = () => {
        startIndex = (startIndex + 1) % placeholders.length;
        const nextPlaceholder = placeholders[startIndex];
        deleteCurrentPlaceholder.call(this, nextPlaceholder).then(() => {
          setTimeout(() => {
            typeInNextPlaceholder.call(this, nextPlaceholder).then(() => {
              setTimeout(showNextPlaceholder.bind(this), delayAfterWordTyped);
            });
          }, delayAfterDeleting);
        });
      };

      window.setTimeout(() => this.panAnimatedPlaceholder(), 350);
      setTimeout(showNextPlaceholder.bind(this), delayBeforeFirstDelete);
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      if (this.input.value.length > 0) {
        this.input.classList.add('search__input--dirty');
      } else {
        this.input.classList.remove('search__input--dirty');
      }
      this.setAnimatedPlaceholderSuspended(
        this.input.value.length > 0 || document.activeElement === this.input
      );
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
    }

    /**
     * Handles the reset button being clicked
     */
    handleReset() {
      this.input.classList.remove('search__input--dirty');
      this.setAnimatedPlaceholderSuspended(document.activeElement === this.input);
    }
  }

  customElements.define('search-form', SearchForm);
}
